import { useState } from 'react';
import { eventApi } from '../services/api';

export interface EventType {
  label: string;
  value: string;
}

export interface TestEventData {
  eventType: EventType;
  customerName: string;
  accountId: string;
  solutionName: string;
  accountManagerEmail: string;
  accountManagerAlias: string;
  creationDate: string;
  isActive: { label: string; value: string };
  daysActive: string;
}

export interface EventResult {
  eventId: string;
  notificationId: number;
  trackingId: string;
  status: string;
  message: string;
  timestamp: string;
  success?: boolean; // Added for frontend UX purposes
}

export function useEventTesting() {
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<EventResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [statistics, setStatistics] = useState<any | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [slackLoading, setSlackLoading] = useState<boolean>(false);
  const [slackResult, setSlackResult] = useState<any | null>(null);

  /**
   * Trigger a test event and get the results
   * @param eventData The event data to use for the test
   * @returns EventResult with the result of the test
   */
  const triggerTestEvent = async (eventData: TestEventData): Promise<EventResult> => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await eventApi.triggerTest(eventData);
      console.log("Test EVENT RESPONSE", response);
      if (response.data && response.data.testResult) {
        // The API returns a testResult object with the event details
        const eventResult: EventResult = {
          ...response.data.testResult,
          success: true // Add success flag
        };
        setResult(eventResult);
        return eventResult;
      }
      
      throw new Error('Invalid response from test event endpoint');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      const errorResult: EventResult = {
        eventId: '',
        notificationId: 0,
        trackingId: '',
        status: 'ERROR',
        message: errorMessage,
        timestamp: new Date().toISOString(),
        success: false
      };
      setError(err instanceof Error ? err : new Error(errorMessage));
      setResult(errorResult);
      console.error('Error triggering test event:', err);
      return errorResult;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get event statistics (stub implementation for now)
   * @returns Statistics object with event counts and metrics
   */
  const getEventStatistics = async (): Promise<any> => {
    setStatsLoading(true);
    setError(null);
    
    try {
      const response = await eventApi.getStatistics();
      
      if (response.data && response.data.data && response.data.data.statistics) {
        const stats = response.data.data.statistics;
        setStatistics(stats);
        return stats;
      }
      
      throw new Error('Invalid response from event statistics endpoint');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(err instanceof Error ? err : new Error(errorMessage));
      console.error('Error fetching event statistics:', err);
      return null;
    } finally {
      setStatsLoading(false);
    }
  };

  /**
   * Test Slack notification using the same event data
   * @param eventData The event data to use for the test
   * @returns ProcessResult with the result of the Slack test
   */
  const testSlackNotification = async (eventData: TestEventData): Promise<any> => {
    setSlackLoading(true);
    setSlackResult(null);
    setError(null);
    
    try {
      const response = await eventApi.testSlack(eventData);
      console.log("Slack Test Response", response);
      
      if (response.data && response.data.processResult) {
        const slackTestResult = {
          ...response.data.processResult,
          success: response.data.processResult.success || false
        };
        setSlackResult(slackTestResult);
        return slackTestResult;
      }
      
      throw new Error('Invalid response from test slack endpoint');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      const errorResult = {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString()
      };
      setError(err instanceof Error ? err : new Error(errorMessage));
      setSlackResult(errorResult);
      console.error('Error testing slack notification:', err);
      return errorResult;
    } finally {
      setSlackLoading(false);
    }
  };

  return {
    loading,
    result,
    error,
    statistics,
    statsLoading,
    slackLoading,
    slackResult,
    triggerTestEvent,
    getEventStatistics,
    testSlackNotification
  };
}

export default useEventTesting;
